import {
  Settings,
  LayoutDashboard,
  Martini,
  MessagesSquare,
  NotebookPen,
  LifeBuoy,
  Grid2X2,
  Library,
  CircleOff,
  Utensils,
  Users,
  UserRound
} from 'lucide-react';

export function getBreadcrumbHref(segments: string[], index: number): string {
  return `/${segments.slice(0, index + 1).join('/')}`;
}

function withSearchParams(url: string, searchParams?: URLSearchParams): string {
  let queryString = searchParams?.toString() || '';
  if (queryString) {
    queryString = `?${queryString}`;
  }
  return `${url}${queryString}`;
}

export function getHref(
  organization_id: string | undefined,
  route: string[],
  searchParams?: URLSearchParams
): string {
  if (!organization_id) {
    return `/${route.join('/')}`;
  }
  return withSearchParams(
    `/${organization_id}/${route.join('/')}`,
    searchParams
  );
}

export const getPublicHref = (
  route: string[],
  searchParams?: URLSearchParams
): string => {
  return withSearchParams(`/${route.join('/')}`, searchParams);
};

/**
 * Same-origin relative path only. Rejects protocol-relative and off-site
 * values so `?from=` cannot be used as an open redirect.
 */
export function safeInternalPath(
  value: string | null | undefined
): string | null {
  if (!value) return null;
  if (!value.startsWith('/')) return null;
  if (value.startsWith('//')) return null;
  if (value.includes('://') || value.includes('\\')) return null;
  return value;
}

export function getHomeHref(organization_id: string): string {
  return getHref(organization_id, [ROUTE.HOME]);
}

export type MenuKind = 'beverage' | 'food';

export function normalizeMenuKind(kind: string | null | undefined): MenuKind {
  return kind === 'food' ? 'food' : 'beverage';
}

/** Beverage menus live under /bev/beverage-menus, food menus under /food/food-menus. */
export function menuListHref(
  organization_id: string,
  kind: string | null | undefined
): string {
  return normalizeMenuKind(kind) === 'food'
    ? getHref(organization_id, [ROUTE.FOOD, ROUTE.FOOD_MENUS])
    : getHref(organization_id, [ROUTE.BEVERAGES, ROUTE.COCKTAIL_MENUS]);
}

/** Kind-aware menu detail URL; `suffix` adds child routes such as `edit`. */
export function menuHref(
  organization_id: string,
  menu: { id: string; kind?: string | null },
  ...suffix: string[]
): string {
  return [
    menuListHref(organization_id, menu.kind),
    encodeURIComponent(menu.id),
    ...suffix
  ].join('/');
}

export function formatSegment(segment: string): string {
  // Replace hyphens with spaces and capitalize each word
  return segment.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
}

const AUTH_ROUTES = {
  SIGN_IN: 'sign-in',
  SIGN_UP: 'sign-up',
  WELCOME: 'welcome'
} as const;

const BEVERAGE_ROUTES = {
  BEVERAGES: 'bev',
  COCKTAIL_MENU: 'cocktail-menu',
  COCKTAIL_MENUS: 'beverage-menus',
  INFO: 'info',
  INVITE: 'invite',
  RECIPES: 'recipes',
  WINE_SPIRITS: 'wine-spirits'
} as const;

const COMMUNICATION_ROUTES = {
  COMMUNICATION: 'communication',
  LOGBOOK: 'logbook',
  MESSAGES: 'messages'
} as const;

const FOOD_ROUTES = {
  ALLERGIES: 'allergies',
  FOOD: 'food',
  FOOD_MENU: 'food-menu',
  FOOD_MENUS: 'food-menus',
  STEPS_OF_SERVICE: 'steps-of-service'
} as const;

const NAVIGATION_ROUTES = {
  API: 'api',
  EIGHTY_SIX: '86list',
  EIGHTY_SIX_ANALYTICS: 'analytics',
  EXPLORE: 'explore',
  HOME: 'dashboard',
  LIBRARY: 'library',
  LIBRARY_MENUS: 'menus',
  LIBRARY_REFERENCE: 'reference'
} as const;

const TEAM_ROUTES = {
  EMPLOYEES: 'employees',
  ONBOARDING: 'onboard',
  TEAM: 'team'
} as const;

const UTILITY_ROUTES = {
  BILLING: 'billing',
  HELP: 'help',
  FEEDBACK: 'feedback',
  LEGAL: 'legal',
  PRICING: 'pricing',
  SETTINGS: 'settings',
  STATUS: 'status',
  UPGRADE: 'upgrade',
  UTILITIES: 'utilities',
  WEBHOOKS: 'webhooks',
  ZERO: 'zero'
} as const;

export const ROUTE = {
  ...AUTH_ROUTES,
  ...BEVERAGE_ROUTES,
  ...COMMUNICATION_ROUTES,
  ...FOOD_ROUTES,
  ...NAVIGATION_ROUTES,
  ...TEAM_ROUTES,
  ...UTILITY_ROUTES
} as const;

const PORTABLE_ORGANIZATION_ROUTES = new Set([
  ROUTE.HOME,
  ROUTE.EIGHTY_SIX,
  `${ROUTE.EIGHTY_SIX}/add`,
  `${ROUTE.EIGHTY_SIX}/${ROUTE.EIGHTY_SIX_ANALYTICS}`,
  ROUTE.LIBRARY,
  `${ROUTE.LIBRARY}/${ROUTE.LIBRARY_MENUS}`,
  `${ROUTE.LIBRARY}/${ROUTE.LIBRARY_REFERENCE}`,
  ROUTE.MESSAGES,
  ROUTE.SETTINGS,
  ROUTE.FEEDBACK,
  ROUTE.LEGAL,
  `${ROUTE.SETTINGS}/${ROUTE.BILLING}`,
  ROUTE.UPGRADE
]);

const MESSAGES_PATH = new RegExp(`^/[^/]+/${ROUTE.MESSAGES}(?:/|$)`);
const EIGHTY_SIX_EDITOR_PATH = new RegExp(
  `^/[^/]+/${ROUTE.EIGHTY_SIX}/(?!${ROUTE.EIGHTY_SIX_ANALYTICS}(?:/|$))(?:add|[^/]+)(?:/|$)`
);

export function isMessagesPath(pathname: string): boolean {
  return MESSAGES_PATH.test(pathname);
}

/**
 * Add-86 and item-detail editors. The list and its analytics page are not
 * editors.
 */
export function isEightySixEditorPath(pathname: string): boolean {
  return EIGHTY_SIX_EDITOR_PATH.test(pathname);
}

/** Chat and 86 editors own the mobile scroll region (flex fill, no tab-bar inset). */
export function ownsMobileScrollRegion(pathname: string): boolean {
  return isMessagesPath(pathname) || isEightySixEditorPath(pathname);
}

/** Never carry an entity id from one organization into another. */
export function getOrganizationSwitchHref(
  pathname: string,
  currentOrganizationId: string | undefined,
  nextOrganizationId: string
): string {
  if (!currentOrganizationId) return getHomeHref(nextOrganizationId);
  const prefix = `/${currentOrganizationId}/`;
  if (!pathname.startsWith(prefix)) return getHomeHref(nextOrganizationId);

  const route = pathname.slice(prefix.length).replace(/\/$/, '');
  return PORTABLE_ORGANIZATION_ROUTES.has(route)
    ? `/${nextOrganizationId}/${route}`
    : getHomeHref(nextOrganizationId);
}

export interface RouteItem {
  id: string;
  href: string;
  label: string;
  icon?: React.ElementType;
  route?: string[];
  size?: 'default' | 'sm' | 'lg';
  children?: RouteItem[];
}

export function isRouteItemActive(pathname: string, item: RouteItem): boolean {
  return (
    pathname === item.href ||
    pathname.startsWith(`${item.href}/`) ||
    (item.children ?? []).some(child => isRouteItemActive(pathname, child))
  );
}

export function getRouteItems(
  organization_id: string,
  t: any
): {
  [key: string]: RouteItem;
} {
  const I18N_ROOT = 'navigation';
  const beverageChildren: RouteItem[] = [
    {
      id: 'beverage-menus',
      route: [ROUTE.BEVERAGES, ROUTE.COCKTAIL_MENUS],
      href: getHref(organization_id, [ROUTE.BEVERAGES, ROUTE.COCKTAIL_MENUS]),
      label: t(`${I18N_ROOT}.${ROUTE.COCKTAIL_MENUS}`),
      icon: Martini
    },
    {
      id: 'cocktail-menu',
      route: [ROUTE.BEVERAGES, ROUTE.COCKTAIL_MENU],
      href: getHref(organization_id, [ROUTE.BEVERAGES, ROUTE.COCKTAIL_MENU]),
      label: t(`${I18N_ROOT}.${ROUTE.COCKTAIL_MENU}`)
    },
    {
      id: 'wine-spirits',
      route: [ROUTE.BEVERAGES, ROUTE.WINE_SPIRITS],
      href: getHref(organization_id, [ROUTE.BEVERAGES, ROUTE.WINE_SPIRITS]),
      label: t(`${I18N_ROOT}.${ROUTE.WINE_SPIRITS}`)
    },
    {
      id: 'info',
      route: [ROUTE.BEVERAGES, ROUTE.INFO],
      href: getHref(organization_id, [ROUTE.BEVERAGES, ROUTE.INFO]),
      label: t(`${I18N_ROOT}.${ROUTE.INFO}`)
    },
    {
      id: 'recipes',
      route: [ROUTE.BEVERAGES, ROUTE.RECIPES],
      href: getHref(organization_id, [ROUTE.BEVERAGES, ROUTE.RECIPES]),
      label: t(`${I18N_ROOT}.${ROUTE.RECIPES}`)
    }
  ];
  const foodChildren: RouteItem[] = [
    {
      id: 'food-menus',
      route: [ROUTE.FOOD, ROUTE.FOOD_MENUS],
      href: getHref(organization_id, [ROUTE.FOOD, ROUTE.FOOD_MENUS]),
      label: t(`${I18N_ROOT}.${ROUTE.FOOD_MENUS}`),
      icon: Utensils
    },
    {
      id: 'allergies',
      route: [ROUTE.FOOD, ROUTE.ALLERGIES],
      href: getHref(organization_id, [ROUTE.FOOD, ROUTE.ALLERGIES]),
      label: t(`${I18N_ROOT}.${ROUTE.ALLERGIES}`)
    },
    {
      id: 'steps-of-service',
      route: [ROUTE.FOOD, ROUTE.STEPS_OF_SERVICE],
      href: getHref(organization_id, [ROUTE.FOOD, ROUTE.STEPS_OF_SERVICE]),
      label: t(`${I18N_ROOT}.${ROUTE.STEPS_OF_SERVICE}`)
    }
  ];

  const recipeItem = beverageChildren.find(item => item.id === 'recipes')!;
  const wineSpiritsItem = beverageChildren.find(
    item => item.id === 'wine-spirits'
  )!;
  const menusItem: RouteItem = {
    id: 'menus',
    route: [ROUTE.LIBRARY, ROUTE.LIBRARY_MENUS],
    href: getHref(organization_id, [ROUTE.LIBRARY, ROUTE.LIBRARY_MENUS]),
    label: t(`${I18N_ROOT}.menus`),
    children: [
      beverageChildren.find(item => item.id === 'beverage-menus')!,
      foodChildren.find(item => item.id === 'food-menus')!
    ]
  };
  const referenceItem: RouteItem = {
    id: 'reference',
    route: [ROUTE.LIBRARY, ROUTE.LIBRARY_REFERENCE],
    href: getHref(organization_id, [ROUTE.LIBRARY, ROUTE.LIBRARY_REFERENCE]),
    label: t(`${I18N_ROOT}.reference`),
    children: [
      beverageChildren.find(item => item.id === 'info')!,
      foodChildren.find(item => item.id === 'allergies')!,
      foodChildren.find(item => item.id === 'steps-of-service')!
    ]
  };

  return {
    [ROUTE.HOME]: {
      id: 'home',
      href: getHomeHref(organization_id),
      label: t(`${I18N_ROOT}.${ROUTE.HOME}`),
      icon: LayoutDashboard
    },
    [ROUTE.EXPLORE]: {
      id: 'explore',
      href: getHref(organization_id, [ROUTE.EXPLORE]),
      label: t(`${I18N_ROOT}.${ROUTE.EXPLORE}`),
      icon: Grid2X2
    },
    [ROUTE.BEVERAGES]: {
      id: 'beverages',
      href: getHref(organization_id, [ROUTE.BEVERAGES]),
      label: t(`${I18N_ROOT}.${ROUTE.BEVERAGES}`),
      icon: Martini,
      children: beverageChildren
    },
    [ROUTE.FOOD]: {
      id: 'food',
      href: getHref(organization_id, [ROUTE.FOOD]),
      label: t(`${I18N_ROOT}.${ROUTE.FOOD}`),
      icon: Utensils,
      children: foodChildren
    },
    [ROUTE.EIGHTY_SIX]: {
      id: '86list',
      href: getHref(organization_id, [ROUTE.EIGHTY_SIX]),
      label: t(`${I18N_ROOT}.${ROUTE.EIGHTY_SIX}`),
      icon: CircleOff
    },
    [ROUTE.LIBRARY]: {
      id: 'library',
      href: getHref(organization_id, [ROUTE.LIBRARY]),
      label: t(`${I18N_ROOT}.${ROUTE.LIBRARY}`),
      icon: Library,
      children: [recipeItem, menusItem, wineSpiritsItem, referenceItem]
    },
    [ROUTE.TEAM]: {
      id: 'team',
      href: getHref(organization_id, [ROUTE.TEAM]),
      label: t(`${I18N_ROOT}.${ROUTE.TEAM}`),
      icon: Users,
      children: [
        {
          id: 'employees',
          route: [ROUTE.EMPLOYEES],
          href: getHref(organization_id, [ROUTE.EMPLOYEES]),
          label: t(`${I18N_ROOT}.${ROUTE.EMPLOYEES}`),
          icon: Users
        }
      ]
    },
    [ROUTE.COMMUNICATION]: {
      id: 'communication',
      href: getHref(organization_id, [ROUTE.COMMUNICATION]),
      label: t(`${I18N_ROOT}.${ROUTE.COMMUNICATION}`),
      icon: NotebookPen,
      children: [
        {
          id: 'messages',
          route: [ROUTE.MESSAGES],
          href: getHref(organization_id, [ROUTE.MESSAGES]),
          label: t(`${I18N_ROOT}.${ROUTE.MESSAGES}`),
          icon: MessagesSquare
        },
        {
          id: 'logbook',
          route: [ROUTE.LOGBOOK],
          href: getHref(organization_id, [ROUTE.LOGBOOK]),
          label: t(`${I18N_ROOT}.${ROUTE.LOGBOOK}`),
          icon: MessagesSquare
        }
      ]
    },
    [ROUTE.UTILITIES]: {
      id: 'utilities',
      href: getHref(organization_id, [ROUTE.UTILITIES]),
      label: t(`${I18N_ROOT}.${ROUTE.UTILITIES}`),
      icon: NotebookPen,
      size: 'sm',
      children: [
        {
          id: 'help',
          route: [ROUTE.HELP],
          href: getHref(organization_id, [ROUTE.HELP]),
          label: t(`${I18N_ROOT}.${ROUTE.HELP}`),
          icon: LifeBuoy
        },
        {
          id: 'settings',
          route: [ROUTE.SETTINGS],
          href: getHref(organization_id, [ROUTE.SETTINGS]),
          label: t(`${I18N_ROOT}.${ROUTE.SETTINGS}`),
          icon: Settings
        },
        {
          id: 'feedback',
          route: [ROUTE.FEEDBACK],
          href: getHref(organization_id, [ROUTE.FEEDBACK]),
          label: t(`${I18N_ROOT}.feedback`),
          icon: NotebookPen
        }
      ]
    }
  };
}

/** Secondary destinations in the mobile navigation menu. */
export function getMoreNavItems(
  organization_id: string,
  t: (key: string) => string
): RouteItem[] {
  const items = getRouteItems(organization_id, t);
  return [
    {
      id: 'logbook',
      href: getHref(organization_id, [ROUTE.LOGBOOK]),
      label: t('navigation.logbook'),
      icon: NotebookPen
    },
    items[ROUTE.LIBRARY],
    { ...items[ROUTE.TEAM], href: getHref(organization_id, [ROUTE.EMPLOYEES]) },
    {
      id: 'profile',
      href: getHref(organization_id, [ROUTE.SETTINGS, 'profile']),
      label: t('navigation.profile'),
      icon: UserRound
    },
    {
      id: 'settings',
      href: getHref(organization_id, [ROUTE.SETTINGS]),
      label: t('navigation.settings'),
      icon: Settings
    }
  ];
}

/** Primary bottom-tab destinations (Home, 86, Shift). */
export function getPrimaryBottomTabs(
  organization_id: string,
  t: (key: string) => string
): RouteItem[] {
  const routeItems = getRouteItems(organization_id, t);
  const messagesTab = routeItems[ROUTE.COMMUNICATION]?.children?.find(
    child => child.id === 'messages'
  );

  return [
    { ...routeItems[ROUTE.HOME], label: t('navigation.home') },
    routeItems[ROUTE.EIGHTY_SIX],
    messagesTab
      ? { ...messagesTab, label: t('navigation.messages') }
      : undefined
  ].filter((tab): tab is RouteItem => Boolean(tab));
}

const BASE_PUBLIC_ROUTES = [
  ROUTE.WELCOME,
  ROUTE.LEGAL,
  ROUTE.INVITE,
  ROUTE.SIGN_IN,
  ROUTE.SIGN_UP,
  ROUTE.PRICING,
  '~offline'
];

export const PUBLIC_ROUTES = [
  ...BASE_PUBLIC_ROUTES.map(routeKey => `/${routeKey}(.*)`),
  ...BASE_PUBLIC_ROUTES.map(routeKey => `(.*)/${routeKey}(.*)`)
];

export const isPublicRoute = (path: string): boolean => {
  const patterns = [
    ...PUBLIC_ROUTES.map(
      pattern => new RegExp(`^${pattern.replace('(.*)', '.*')}$`)
    ),
    new RegExp(`^/${ROUTE.API}/${ROUTE.STATUS}.*$`)
  ];

  return patterns.some(pattern => pattern.test(path));
};

// Org-create at /onboard (no org id). Separate from /{org}/welcome.
export const isOnboardingRoute = (path: string): boolean => {
  return path.includes(`/${ROUTE.ONBOARDING}`);
};
